import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface HomePageVideoProps {
  className?: string;
}

interface Settings {
  homepageVideo?: {
    url: string;
    fileName: string;
  };
}

interface SettingsResponse {
  success: boolean;
  data?: Settings;
}

const StrategicAIVideo: React.FC<HomePageVideoProps> = ({ className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [videoUrl, setVideoUrl] = useState('/images/hero.mp4');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        console.log('Fetching settings...');
        const response = await axios.get<SettingsResponse>('/api/settings');
        
        if (response.data.success && response.data.data) {
          if (response.data.data.homepageVideo?.url) {
            setVideoUrl(getFullVideoUrl(response.data.data.homepageVideo.url));
          }
        }
      } catch (err: any) {
        console.error('Error loading settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.currentTarget;
    let errorDetails = 'Unknown error';
    
    if (videoElement.error) {
      switch (videoElement.error.code) {
        case 1:
          errorDetails = 'MEDIA_ERR_ABORTED: Fetching process aborted by user';
          break;
        case 2:
          errorDetails = 'MEDIA_ERR_NETWORK: Network error while loading';
          break;
        case 3:
          errorDetails = 'MEDIA_ERR_DECODE: Media decoding error';
          break;
        case 4:
          errorDetails = 'MEDIA_ERR_SRC_NOT_SUPPORTED: Format not supported';
          break;
        default:
          errorDetails = `Unknown error (${videoElement.error.code})`;
      }
    }
    
    console.error(`Video failed to load: ${videoUrl}. Error: ${errorDetails}`);
    
    if (videoUrl !== '/images/hero.mp4') {
      console.warn('Falling back to static default video: /images/hero.mp4');
      setVideoUrl('/images/hero.mp4');
    } else {
      setVideoError(true);
    }
  };

  const getFullVideoUrl = (url: string) => {
    // If the URL is already absolute (starts with http or https), return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If URL starts with /uploads or /images, it's from our server's static directory
    if (url.startsWith('/uploads') || url.startsWith('/images')) {
      return url;
    }
    
    // Otherwise, prepend with uploads path
    return `/uploads/${url}`;
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
      videoRef.current.play().catch(err => {
        console.warn('Autoplay failed or was prevented:', err);
      });
    }
  }, [videoUrl]);

  if (loading) {
    return <div className={`rounded-lg overflow-hidden aspect-video bg-gray-200 dark:bg-gray-700 animate-pulse ${className}`} />;
  }

  if (videoError) {
    return (
      <div className={`rounded-lg overflow-hidden aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center ${className}`}>
        <div className="text-gray-500 dark:text-gray-400 text-center p-4">
          <p>Video unavailable</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg overflow-hidden shadow-lg pointer-events-none select-none ${className}`}>
      <video 
        ref={videoRef}
        className="w-full h-auto pointer-events-none select-none"
        src={videoUrl}
        autoPlay
        muted
        loop
        playsInline
        onError={handleVideoError}
      />
    </div>
  );
};

export default StrategicAIVideo; 